import { cssThemedValue } from '@creditornot/cb-ingredients';
import React, { ReactNode } from 'react';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT, useIsMobile } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';

import Tooltip from './Tooltip';

type CampaignFormItemProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  value: ReactNode;
  tooltip?: {
    title?: React.ReactNode;
    description?: React.ReactNode;
  };
  isBorderHidden?: boolean;
  'data-testid'?: string;
};

const CampaignFormItemContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  margin: 0 auto;
  border: 1px solid;
  border-color: ${cssThemedValue('borderPrimary')};
  padding: 16px;
  border-radius: 8px;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const StyledTooltip = styled(Tooltip)`
  line-height: 0;
`;

const CampaignFormViewContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    &:not(:last-child) {
      margin-block-end: 24px;
    }
  }
`;

const StyledActionWrapper = styled.div`
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
    width: 100%;

    > * {
      flex: 1;
    }
  }
`;

const TitleBlock = styled.div`
  margin-block-end: 4px;
`;

const TopRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

export const CampaignFormView = ({ title, subtitle, action, tooltip, value }: CampaignFormItemProps) => {
  return (
    <CampaignFormViewContainer>
      {(title || tooltip) && (
        <TopRow>
          <TitleBlock>
            <T.Heading6>
              {title}
              {tooltip && <StyledTooltip {...tooltip} />}
            </T.Heading6>
            {!!subtitle && <T.Body3>{subtitle}</T.Body3>}
          </TitleBlock>
          {action}
        </TopRow>
      )}
      {value}
    </CampaignFormViewContainer>
  );
};

const CampaignFormItem = ({ title, subtitle, tooltip, value, action, ...props }: CampaignFormItemProps) => {
  const isMobile = useIsMobile();
  const actionElement = action && <StyledActionWrapper>{action}</StyledActionWrapper>;
  return (
    <CampaignFormItemContainer data-testid={props['data-testid']}>
      <CampaignFormView
        title={title}
        tooltip={tooltip}
        value={value}
        subtitle={subtitle}
        action={!isMobile && actionElement}
      />
      {isMobile && actionElement}
    </CampaignFormItemContainer>
  );
};

export default CampaignFormItem;
